import React from "react";
import { PAGE_SIZE_OPTIONS } from "../Commend3Constants";

type ModelsPaginationProps = {
  page: number;
  pageSize: number;
  total: number;
  onPage: (next: number) => void;
  onPageSize: (next: number) => void;
};

const ARROW_BASE: React.CSSProperties = {
  width: "28px",
  height: "28px",
  borderRadius: "6px",
  border: "0.5px solid #E3E5EC",
  background: "transparent",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const Chevron = ({ back }: { back: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    className="tw-h-3.5 tw-w-3.5"
    style={{
      stroke: "currentColor",
      fill: "none",
      strokeWidth: 1.8,
      strokeLinecap: "round",
      strokeLinejoin: "round",
    }}
    aria-hidden="true"
  >
    <polyline points={back ? "15 18 9 12 15 6" : "9 18 15 12 9 6"} />
  </svg>
);

const ModelsPagination = ({
  page,
  pageSize,
  total,
  onPage,
  onPageSize,
}: ModelsPaginationProps) => {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pageCount);
  const from = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const to = Math.min(current * pageSize, total);
  const canPrev = current > 1;
  const canNext = current < pageCount;

  return (
    <div
      id="commend3-pagination"
      className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-px-4 tw-py-2.5"
      style={{ borderTop: "0.5px solid #E3E5EC" }}
    >
      <span className="tw-text-[12px] tw-text-[#59596E]">
        {total === 0 ? "No models" : `${from}–${to} of ${total}`}
      </span>
      <span
        className="tw-mx-0.5"
        style={{ width: "1px", height: "14px", background: "#E3E5EC" }}
      />
      <span className="tw-text-[12px] tw-text-[#59596E]">Show</span>
      <select
        id="commend3-page-size"
        className="tw-cursor-pointer tw-rounded-md tw-bg-white tw-px-[7px] tw-py-[3px] tw-text-[12px] tw-text-[#0B0C23]"
        style={{ border: "0.5px solid #E3E5EC" }}
        value={pageSize}
        onChange={(event) => onPageSize(Number(event.target.value))}
        aria-label="Records on page select"
      >
        {PAGE_SIZE_OPTIONS.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      <div className="tw-ml-1 tw-flex tw-gap-0.5">
        <button
          id="commend3-page-prev"
          type="button"
          title="Previous page"
          style={{
            ...ARROW_BASE,
            color: canPrev ? "#59596E" : "#B4B9D4",
            cursor: canPrev ? "pointer" : "not-allowed",
            opacity: canPrev ? 1 : 0.5,
          }}
          onClick={() => canPrev && onPage(current - 1)}
        >
          <Chevron back />
        </button>

        {Array.from({ length: pageCount }, (_, index) => index + 1).map((n) => (
          <button
            key={n}
            id={`commend3-page-${n}`}
            type="button"
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "6px",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              border: n === current ? "0.5px solid transparent" : "0.5px solid #E3E5EC",
              background: n === current ? "#030562" : "transparent",
              color: n === current ? "#FFFFFF" : "#59596E",
            }}
            onClick={() => onPage(n)}
          >
            {n}
          </button>
        ))}

        <button
          id="commend3-page-next"
          type="button"
          title="Next page"
          style={{
            ...ARROW_BASE,
            color: canNext ? "#59596E" : "#B4B9D4",
            cursor: canNext ? "pointer" : "not-allowed",
            opacity: canNext ? 1 : 0.5,
          }}
          onClick={() => canNext && onPage(current + 1)}
        >
          <Chevron back={false} />
        </button>
      </div>
    </div>
  );
};

export default ModelsPagination;
